import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [details, setDetails] = useState(null);

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      const orderId = localStorage.getItem('pending_order_id');
      const planId = localStorage.getItem('pending_plan');

      if (!orderId) {
        setStatus('failed');
        return;
      }

      // Wait a bit for webhook to process
      await new Promise(resolve => setTimeout(resolve, 3000));

      const response = await axios.get(`/api/payments/status/${orderId}`);

      if (response.data.success) {
        const paymentStatus = response.data.status;

        if (paymentStatus === 'COMPLETED' || paymentStatus === 'PAYMENT_SUCCESS') {
          setStatus('success');
          setDetails({
            transactionId: response.data.transaction_id,
            amount: response.data.amount,
            planId: planId
          });
          
          // Clear stored IDs
          localStorage.removeItem('pending_order_id');
          localStorage.removeItem('pending_plan');
        } else if (paymentStatus === 'PENDING') {
          // Retry after delay
          setTimeout(verifyPayment, 3000);
        } else {
          setStatus('failed');
        }
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0F2A] via-[#151827] to-[#0A0F2A] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-[#1A1B2E] to-[#151827] border border-gray-700 rounded-3xl p-8 md:p-12 max-w-md w-full text-center shadow-2xl"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-cyan-400 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-3">Verifying Payment...</h2>
            <p className="text-gray-400">Please wait while we confirm your transaction</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-3">Payment Successful! 🎉</h2>
            <p className="text-gray-300 mb-6">Your subscription is now active</p>
            
            {details && (
              <div className="bg-black/30 rounded-xl p-4 mb-6 text-left">
                <div className="text-sm text-gray-400 mb-1">Transaction ID</div>
                <div className="text-white font-mono text-xs break-all mb-3">{details.transactionId}</div>
                <div className="text-sm text-gray-400 mb-1">Amount Paid</div>
                <div className="text-2xl font-bold text-cyan-400">₹{(details.amount / 100).toFixed(2)}</div>
              </div>
            )}

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-4 rounded-xl hover:opacity-90 transition-opacity"
              data-testid="goto-dashboard"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <AlertCircle className="w-20 h-20 text-red-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-3">Payment Failed</h2>
            <p className="text-gray-300 mb-6">Your payment could not be processed</p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/pricing')}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-4 rounded-xl hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gray-700 text-white font-semibold py-3 rounded-xl hover:bg-gray-600 transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
